import { useCallback, useEffect, useRef, useState } from 'react';
import { api, streamUrl, type Camera, type StreamReady } from '../lib/api';
import { mulaiHeartbeat, pakaiAliran, refAliranMjpeg } from '../lib/streamClient';
import { mintaLayarPenuh } from '../lib/layarPenuh';
import { usePanahTv, type ArahPanah } from '../lib/panahTv';
import { useBahasa } from '../i18n';
import { useZoomGambar } from '../lib/zoomGambar';

interface Props {
  camera: Camera;
  onClose: () => void;
  /**
   * Pindah kamera tanpa keluar dari layar penuh (23 Agustus 2026).
   *
   * Diminta lapangan: *"Lagi dalam mode Camera Fullscreen, gw bisa next ke
   * kamera berikutnya?"* Tanpa ini urutannya tutup → cari tile berikutnya →
   * buka lagi, dan setiap kali itu berarti keluar-masuk layar penuh browser.
   *
   * Opsional: `/cameras` dan `/dashboard` sama-sama punya daftar untuk
   * dijelajahi, tapi pemanggil lain belum tentu punya.
   */
  onPrev?: () => void;
  onNext?: () => void;
  /** Posisi dalam daftar yang sedang dijelajahi, mis. 3 dari 94. */
  posisi?: { index: number; total: number };
  /**
   * Pemanggilnya sudah memegang layar penuh browser (mode layar penuh grid
   * §G8.2) — viewer ini menumpang di atasnya (25 Agustus 2026).
   *
   * Dua pemilik untuk satu layar penuh browser tidak bisa akur: yang kedua
   * memanggil `requestFullscreen` di atas layar penuh yang sudah ada, lalu
   * `exitFullscreen` miliknya saat pergi — dan keluarnya itu dibaca pemanggil
   * sebagai "orangnya menekan Esc", jadi grid layar penuhnya ikut dibongkar.
   * Hasilnya yang dilaporkan lapangan: menutup kamera mendarat di dashboard,
   * bukan kembali ke gridnya.
   *
   * Saat menumpang, yang menutup viewer ini adalah pemanggilnya (lewat
   * `fullscreenchange`-nya sendiri) atau `onClose` di bawah — bukan layar
   * penuh browser yang lepas.
   */
  bersarang?: boolean;
}

type Phase =
  /** `sub` masih tampil, worker HD sedang disiapkan. */
  | { kind: 'menunggu' }
  /** HD siap, `<img>` main sedang memuat frame pertamanya. */
  | { kind: 'menukar'; nonce: number }
  | { kind: 'hd'; nonce: number }
  /** Kamera menolak sesi HD (§D1.2) — tetap `sub`, dengan label jujur. */
  | { kind: 'terbatas'; message: string };

/**
 * Tampilan layar penuh satu kamera, mengikuti urutan §D3.1:
 *
 * 1. `sub` sudah tampil sejak dari tile.
 * 2. Overlay "Menunggu Resolusi HD" muncul — **`sub` tidak dimatikan**.
 * 3. Backend menyalakan worker `main` dan membalas begitu frame pertama ada.
 * 4. `src` ditukar ke `main`, tunggu `onLoad`, baru overlay dilepas dan `sub`
 *    dijatuhkan.
 *
 * Tidak boleh ada frame hitam di antaranya — itu yang membuat transisi terasa
 * patah. Karena itu kedua `<img>` hidup bersamaan selama langkah 4, dengan
 * `main` ditumpuk di atas `sub`.
 */
export function FullscreenViewer({
  camera,
  onClose,
  onPrev,
  onNext,
  posisi,
  bersarang = false,
}: Props) {
  const { t } = useBahasa();
  const [phase, setPhase] = useState<Phase>({ kind: 'menunggu' });
  const [hdLoaded, setHdLoaded] = useState(false);
  const subNonce = useRef(Date.now());
  const closeRef = useRef<HTMLButtonElement>(null);

  /**
   * `onClose` datang sebagai arrow inline dari pemanggilnya, jadi identitasnya
   * berubah tiap render. Efek layar penuh di bawah **tidak boleh** ikut berubah
   * karenanya: menjalankannya ulang berarti `exitFullscreen` lalu
   * `requestFullscreen` lagi di setiap render — layar berkedip, dan permintaan
   * kedua ditolak browser karena tidak lagi berasal dari gestur pemakai.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    let batal = false;

    /**
     * Kamera berganti = mulai dari awal urutan §D3.1 lagi.
     *
     * Ini yang membuat pindah-kamera tidak boleh dilakukan dengan `key={id}` di
     * pemanggilnya: remount melepas `mintaLayarPenuh()` lalu memintanya lagi,
     * dan permintaan kedua tidak lagi berasal dari gestur pemakai — browser
     * menolaknya dan layar penuhnya hilang di tengah jalan. Jadi komponennya
     * bertahan dan keadaannya yang direset.
     *
     * `subNonce` ikut dibarui: tanpa itu `<img>` sub tetap menempel pada
     * koneksi MJPEG kamera sebelumnya.
     */
    setPhase({ kind: 'menunggu' });
    setHdLoaded(false);
    subNonce.current = Date.now();
    // Zoom ikut disetel ulang: 3× di pojok kiri atas kamera lorong tidak berarti
    // apa-apa di kamera gerbang, dan orangnya mendarat di bidang yang buram
    // tanpa tahu kenapa.
    resetZoom();

    void (async () => {
      try {
        const r = await api<StreamReady>(`/stream/${camera.id}/main/ready`);
        if (batal) return;
        if (r.ready) setPhase({ kind: 'menukar', nonce: Date.now() });
        else
          setPhase({
            kind: 'terbatas',
            message: r.message ?? t('This camera cannot provide an HD stream right now.'),
          });
      } catch {
        if (!batal)
          setPhase({ kind: 'terbatas', message: t('The HD stream could not be started.') });
      }
    })();

    return () => {
      batal = true;
    };
  }, [camera.id]);

  // Fokus dipindahkan ke tombol tutup: pembaca layar mengumumkan overlay, dan
  // Esc punya sasaran yang jelas.
  useEffect(() => closeRef.current?.focus(), []);

  /*
   * Kiri/Kanan diklaim hanya kalau memang ada kamera sebelahnya — kalau tidak,
   * penangan di bawah tidak mencegah apa pun dan panahnya lebih berguna dipakai
   * remote untuk menjangkau tombol tutup dan zoom.
   */
  usePanahTv(
    useCallback(
      (arah: ArahPanah) => (arah === 'right' ? !!onNext : arah === 'left' ? !!onPrev : false),
      [onNext, onPrev],
    ),
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Panah = kamera sebelah, arah yang sama dengan tombolnya. Di dashboard
      // panah memindahkan kursor tile, tapi selama viewer terbuka halaman itu
      // memang menyerahkan papan ketik ke sini.
      if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      }
      if (e.key === 'ArrowLeft' && onPrev) {
        e.preventDefault();
        onPrev();
      }
      /**
       * Zoom lewat papan ketik (§G7 no.3: setiap aksi punya jalan lain).
       *
       * Gestur cubit dan roda tetikus tidak ada di TV pos satpam — di sana
       * yang ada cuma papan ketik, dan fitur yang cuma bisa dipanggil dengan
       * jari adalah fitur yang tidak ada di ruangan itu. Titik acuannya pusat
       * layar: tanpa penunjuk, tidak ada titik lain yang bisa disebut.
       */
      if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '0') {
        e.preventDefault();
        if (e.key === '0') {
          resetZoom();
          return;
        }
        zoomTengah(e.key === '-' ? 1 / 1.4 : 1.4);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNext, onPrev]);

  // Aliran di sini juga milik tab ini — sapaannya wajib hidup selama viewer
  // terbuka (§D3.4), termasuk saat dashboard di belakangnya sudah berhenti.
  useEffect(() => mulaiHeartbeat(), []);

  /*
   * Kedua aliran viewer ini ikut ke dalam daftar sapaan (§D3.4 aturan 3), dan
   * masing-masing berhenti pada saatnya sendiri: substream begitu HD termuat,
   * HD begitu viewer ditutup. Yang `main` adalah aliran termahal di aplikasi —
   * satu yang tertinggal saja sudah cukup untuk menjelaskan "kamera cuma empat
   * kok 25 Mbps".
   */
  useEffect(() => {
    if (phase.kind === 'hd') return;
    return pakaiAliran(camera.id, 'sub');
  }, [camera.id, phase.kind]);

  useEffect(() => {
    if (phase.kind !== 'menukar' && phase.kind !== 'hd') return;
    return pakaiAliran(camera.id, 'main');
  }, [camera.id, phase.kind]);

  /**
   * Zoom digital (§G8.2, 25 Agustus 2026, diminta lapangan).
   *
   * Disimpan di sini, bukan di `<img>`-nya: kedua gambar (`sub` dan `main`)
   * hidup bersamaan selama transisi §D3.1 langkah 4, dan keduanya harus
   * diperbesar dengan angka yang sama persis — kalau tidak, pertukaran HD akan
   * terlihat seperti gambar yang meloncat.
   */
  const kotakRef = useRef<HTMLDivElement>(null);
  /**
   * Gestur zoom (§G8.2) tinggal di `lib/zoomGambar` sejak 31 Agu 2026 — halaman
   * Playback memakai yang sama persis (§G10.6), dan dua salinan gestur berarti
   * dua tempat yang harus sepakat soal apa arti satu klik roda.
   */
  const { zoom, gaya: gayaZoom, reset: resetZoom, zoomTengah, handlers: zoomHandlers } =
    useZoomGambar(kotakRef);
  /**
   * Kedua gambar di sini adalah aliran MJPEG yang harus dibatalkan sendiri saat
   * lepas — dan yang `main` adalah aliran termahal di seluruh aplikasi.
   *
   * Substream lepas di tengah jalan, bukan cuma saat viewer ditutup: begitu HD
   * termuat, `tampilkanSub` jadi `false`. Tanpa pembatalan, satu kali buka-tutup
   * layar penuh meninggalkan dua aliran hidup — satu HD resolusi penuh — sampai
   * seluruh tile di tab ini berhenti dan penyapu §D3.4 baru boleh bertindak.
   *
   * `useRef(...).current` supaya identitas ref-nya tetap: ref yang berganti tiap
   * render akan membatalkan aliran yang sedang hidup.
   */
  const refSub = useRef(refAliranMjpeg()).current;
  const refHd = useRef(refAliranMjpeg()).current;

  /**
   * Dibaca sekali saat viewer dibuka: keadaan "menumpang" tidak boleh berubah
   * di tengah hidupnya, karena efek di bawah sengaja hanya jalan sekali.
   */
  const bersarangAwal = useRef(bersarang).current;

  /**
   * Layar penuh browser sungguhan, bukan sekadar overlay setinggi viewport:
   * yang diminta operator adalah gambar mengisi layar TV/HP/desktop tanpa bilah
   * alamat dan tanpa chrome aplikasi — pola aplikasi DVR.
   *
   * **Esc harus sekali tekan, bukan dua.** Saat layar penuh browser aktif, Esc
   * dimakan browser untuk keluar dari layar penuh dan `keydown`-nya tidak pernah
   * sampai ke halaman — jadi penekanan pertama cuma mengecilkan gambar dan
   * viewer-nya tetap terbuka, menyisakan satu kamera memenuhi panel dashboard
   * sampai orangnya menekan Esc kedua kali. Tombol "Close (Esc)" tidak kena
   * karena ia memanggil `onClose` langsung.
   *
   * Jadi keluarnya layar penuh **itu sendiri** yang dijadikan sinyal tutup —
   * pola yang sama sudah dipakai slideshow (§G8.1). Ia juga menangkap F11 dan
   * tombol keluar milik browser, bukan cuma Esc.
   *
   * Semua itu berlaku untuk viewer yang **memiliki** layar penuhnya. Kalau ia
   * menumpang (`bersarang`), seluruh urusan ini milik pemanggilnya — lihat
   * `Props.bersarang`.
   */
  useEffect(() => {
    // Menumpang: layar penuhnya milik pemanggil, jadi tidak ada yang diminta
    // dan tidak ada yang dikembalikan di sini.
    if (bersarangAwal) return;

    const keluar = mintaLayarPenuh();

    // Baru berlaku setelah kita benar-benar masuk. Tanpa penjaga ini, event
    // pertama yang datang saat permintaan layar penuh ditolak (tanpa gestur
    // pemakai, iOS Safari, kebijakan iframe) akan langsung menutup viewer yang
    // baru saja dibuka.
    let pernahMasuk = false;
    const onChange = () => {
      if (document.fullscreenElement) pernahMasuk = true;
      else if (pernahMasuk) onCloseRef.current();
    };

    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      keluar();
    };
    // Sekali saat viewer dibuka, sekali saat ditutup — lihat `onCloseRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bersarangAwal]);

  const tampilkanSub = phase.kind !== 'hd';
  const overlayTampil = phase.kind === 'menunggu' || (phase.kind === 'menukar' && !hdLoaded);

  return (
    <div
      className="fixed inset-0 z-50 bg-black"
      /* Cubit dan geser di sini miliknya sendiri — pembungkus APK tidak boleh
         membacanya sebagai tarik-untuk-muat-ulang (`lib/muatUlangAndroid.ts`). */
      data-tanpa-muat-ulang=""
      role="dialog"
      aria-modal="true"
      aria-label={t('{nama} — fullscreen', { nama: camera.name })}
    >
      {/* Kotak gambar sekaligus kotak gestur (§G8.2, 25 Agustus 2026).

          `touch-action: none` yang membuat cubitan sampai ke sini: tanpa itu
          browser mengambilnya lebih dulu untuk zoom halaman, dan yang membesar
          adalah seluruh aplikasi — tombol, bilah, gradien nama kamera — bukan
          gambarnya.

          Kotaknya juga yang jadi acuan koordinat: semua perhitungan zoom di
          atas memakai piksel relatif terhadap sisi kiri-atasnya. */}
      <div
        ref={kotakRef}
        className={`absolute inset-0 touch-none select-none overflow-hidden ${
          zoom.s > 1 ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        {...zoomHandlers}
      >
        {/* Satu pembungkus untuk kedua gambar — lihat `zoom`. `origin-top-left`
            wajib: rumus zoom-ke-titik di atas menganggap titik acuannya 0,0,
            sedangkan bawaan CSS adalah tengah. */}
        <div
          className="absolute inset-0"
          style={gayaZoom}
        >
          {tampilkanSub && (
            <img
              ref={refSub}
              src={streamUrl(camera.id, 'sub', subNonce.current)}
              alt=""
              draggable={false}
              className="absolute inset-0 h-full w-full object-contain"
            />
          )}

          {(phase.kind === 'menukar' || phase.kind === 'hd') && (
            <img
              ref={refHd}
              src={streamUrl(camera.id, 'main', phase.nonce)}
              alt=""
              draggable={false}
              onLoad={() => {
                setHdLoaded(true);
                setPhase({ kind: 'hd', nonce: phase.nonce });
              }}
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-200 ${
                hdLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )}
        </div>

        {/* TIDAK ada tirai gelap di atas gambarnya, dan itu perubahan yang
            paling terasa (23 Agustus 2026).

            Sebelumnya masa tunggu HD ditutupi `bg-black/45` + spinner besar di
            tengah layar. Substream-nya sendiri sudah mengalir penuh sepanjang
            waktu itu — gambar hidup, jam berjalan, orang lewat — tapi
            diredupkan dan diberi tulisan "Switching to HD…", jadi yang dilihat
            orang bukan kamera melainkan **layar tunggu**. Itu yang membuat
            beberapa detik terasa "terlalu lama": bukan durasinya, tapi
            kenyataan bahwa selama itu ia disuruh menunggu alih-alih menonton.

            Penggantinya satu chip kecil di bawah, sebaris dengan nama kamera —
            tempat penanda "HD" akan muncul begitu selesai, jadi yang berubah
            cuma isi chip-nya. Tetap `role="status"` supaya pembaca layar tahu
            ada yang sedang berlangsung. */}
      </div>

      {/* Nama kamera **di dalam** gambar, bawah tengah (§G7 no.5) — bukan bilah
          judul yang memotong tinggi layar. Gradien tipis di belakangnya supaya
          teks tetap terbaca di atas adegan terang maupun gelap, dan
          `pointer-events-none` supaya ia tidak pernah menghalangi apa pun. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-4 pb-6 pt-12 text-white">
        <span className="text-lg font-medium drop-shadow">{camera.name}</span>
        <span className="flex items-center gap-2">
          {phase.kind === 'terbatas' && (
            <span
              className="rounded bg-amber-500 px-2 py-0.5 text-xs font-semibold text-black"
              title={phase.message}
            >
              {t('Limited quality — {pesan}', { pesan: phase.message })}
            </span>
          )}
          {overlayTampil && (
            <span
              role="status"
              className="flex items-center gap-1.5 rounded bg-white/15 px-2 py-0.5 text-xs font-semibold"
            >
              <span className="h-3 w-3 animate-spin rounded-full border border-white/40 border-t-white motion-reduce:animate-none" />
              HD…
            </span>
          )}
          {phase.kind === 'hd' && (
            <span className="rounded bg-white/15 px-2 py-0.5 text-xs font-semibold">HD</span>
          )}
          {/* Angka zoom muncul hanya kalau memang sedang diperbesar. Ia jawaban
              atas "kenapa gambarnya beda dari tadi" — dan sekaligus penanda
              bahwa ada keadaan yang perlu dikembalikan (ketuk ganda, atau `0`). */}
          {zoom.s > 1 && (
            <span
              className="rounded bg-white/15 px-2 py-0.5 text-xs font-semibold tabular-nums"
              role="status"
            >
              {zoom.s.toFixed(1)}×
            </span>
          )}
          {/* Posisi selalu disebut lengkap, bukan sekadar panah (§G8) — tanpa
              angkanya tidak ada cara tahu masih ada berapa lagi di depan. */}
          {posisi && posisi.total > 1 && (
            <span className="rounded bg-black/40 px-2 py-0.5 text-xs tabular-nums text-white/80">
              {posisi.index + 1} / {posisi.total}
            </span>
          )}
        </span>
      </div>

      {/* Kamera sebelah. Di tepi kiri/kanan setinggi tengah layar — jauh dari
          tombol tutup di pojok kanan atas, dan jauh dari nama kamera di bawah
          tengah. Keduanya tetap terlihat, tidak menyembunyikan diri: mode ini
          selalu punya orang yang sedang memegangnya, beda dengan slideshow yang
          ditinggal menyala semalaman (§G8.2). */}
      {onPrev && <TombolGeser arah="prev" onClick={onPrev} />}
      {onNext && <TombolGeser arah="next" onClick={onNext} />}

      {/* Tombol tutup pindah ke pojok kanan atas, di atas gambar. Ia tetap
          elemen pertama yang menerima fokus (lihat `closeRef`), jadi Esc dan
          papan ketik tetap punya sasaran yang jelas. */}
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 z-10 flex min-h-tap items-center rounded-md bg-black/50 px-4 text-sm text-white ring-1 ring-white/20 transition-[background-color,scale] duration-150 hover:bg-black/80 active:scale-[0.96]"
      >
        {t('Close (Esc)')}
      </button>
    </div>
  );
}

/** Tombol kamera sebelah di tepi layar penuh. */
function TombolGeser({ arah, onClick }: { arah: 'prev' | 'next'; onClick: () => void }) {
  const { t } = useBahasa();
  const prev = arah === 'prev';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={prev ? t('Previous camera') : t('Next camera')}
      title={prev ? t('Previous camera (←)') : t('Next camera (→)')}
      className={`absolute top-1/2 z-10 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white ring-1 ring-white/20 transition-[background-color,scale] duration-150 hover:bg-black/80 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
        prev ? 'left-3' : 'right-3'
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={prev ? 'm15 6-6 6 6 6' : 'm9 6 6 6-6 6'} />
      </svg>
    </button>
  );
}
